import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import {
  IconAddWindow,
  IconBookmarkPlus,
  IconFolderPlus,
  IconFolderEdit,
} from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import CodeNav from '../components/code/CodeNav'
import CodeBreadcrumb from '../components/code/CodeBreadcrumb'
import CodeViewToggle from '../components/code/CodeViewToggle'
import CodeFilterPanel from '../components/code/CodeFilterPanel'
import CodeWindowBody from '../components/code/CodeWindowBody'
import useCodeWorkspace from '../components/code/useCodeWorkspace'
import { useBookmarks } from '../hooks/useBookmarks'
import { useTopics } from '../hooks/useTopics'
import { useSearchroomFilterOptions } from '../hooks/useSearchroomFilterOptions'
import { useModal } from '../context/ModalContext'
import { MultiSelect } from '../components/inputs'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { CODE_GRID_KEY, CODE_WINDOW_LIMITS } from '../components/code/openInCode'
import { usePageTitle } from '../components/DocumentTitle'
import { standardName } from '../utils/standardRef'
import { topicLabelFor } from '../utils/topicLabel'

// Code (standards) workspace. Same shell as the Searchrooms page: a collapsible
// sidebar beside a tiled window grid. Each window carries its own Library nav
// state (position + filter); the sidebar always mirrors the active window, so
// switching windows swaps the tree instantly. `useCodeWorkspace` owns that
// per-window map and the shared item cache.
function CodePage() {
  const { openModal } = useModal()
  const [notice, setNotice] = useState('')
  const [filterOpen, toggleFilter] = useFilterOpen('ct-code-filter-open')

  const bookmarks = useBookmarks()
  const topicsData = useTopics()

  const grid = useWindowGrid({
    storageKey: CODE_GRID_KEY,
    limits: CODE_WINDOW_LIMITS,
    initialType: 'code',
  })

  const workspace = useCodeWorkspace(grid, bookmarks, topicsData.topics)

  // Facet option lists for the filter popover.
  const filterOptions = useSearchroomFilterOptions()

  // Auto-dismiss the "too many windows" notice.
  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  // Add a new window: seed it as a copy of the active window's nav state, then
  // create it (honoring the soft cap). The new window becomes active, so the
  // sidebar shows the copied tree/filter.
  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    workspace.prepareWindowCopy()
    grid.addWindow({ type: 'code' })
  }, [grid, workspace])

  // A window's content is driven by its own nav state (the opened item object),
  // so the title + body resolve from the per-window map rather than grid entityId.
  const { navById, treeFor } = workspace
  const activeNav = navById[grid.activeId]
  usePageTitle(standardName(activeNav?.open?.standard) || '')

  const getTitle = useCallback(
    (win) => {
      const item = navById[win.id]?.open
      // The window header holds only the standard name; the item label lives in
      // the viewer pane header.
      return item ? standardName(item.standard) || item.label : 'New window'
    },
    [navById]
  )

  // Reveal a window's live viewer section in the sidebar: focus that window (so
  // the sidebar mirrors it) then navigate/scroll its nav to the section.
  const handleReveal = useCallback(
    (windowId, trail) => {
      grid.focusWindow(windowId)
      workspace.revealItem(windowId, trail)
    },
    [grid, workspace]
  )

  // A comment or guide chip whose section is in another standard: focus this
  // window and open that document on the section.
  const handleOpenStandard = useCallback(
    (windowId, anchor) => {
      grid.focusWindow(windowId)
      workspace.openInWindow(windowId, anchor)
    },
    [grid, workspace]
  )

  const renderWindow = useCallback(
    (win) => {
      const nav = navById[win.id]
      const item = nav?.open || null
      return (
        <CodeWindowBody
          item={item}
          items={item ? treeFor(item.standard) : undefined}
          windowId={win.id}
          panes={win.panes}
          onPanesChange={(layout) => grid.setPanes(win.id, layout)}
          onRevealInNav={(trail) => handleReveal(win.id, trail)}
          onOpenStandard={(anchor) => handleOpenStandard(win.id, anchor)}
          history={nav?.history}
          historyIndex={nav?.historyIndex}
          recall={nav?.recall}
          onVisit={(section) => workspace.visit(win.id, section)}
          onHistory={(dir) => workspace.stepHistory(win.id, dir)}
        />
      )
    },
    [navById, treeFor, grid, handleReveal, handleOpenStandard, workspace]
  )

  // Label click opens the row in the active window's viewer: a standard at root
  // loads the full standard; an item inside a standard jumps to its page. The
  // forward arrow is what enters a standard / drills a level.
  const handleOpen = useCallback((row) => workspace.select(row), [workspace])
  const handleForward = useCallback(
    (row) => {
      if (workspace.atRoot) workspace.enterStandard(row)
      else workspace.drillInto(row)
    },
    [workspace]
  )

  const isRowBookmarked = useCallback((row) => bookmarks.isBookmarked(row._id), [bookmarks])
  const topicLabel = useMemo(() => topicLabelFor(topicsData.topics), [topicsData.topics])
  const bookmarkTopics = useCallback(
    (row) => {
      const bookmark = bookmarks.getBookmark(row._id)
      return (bookmark?.topics || [])
        .map((topic) => ({
          id: String(topic?._id || topic),
          label: topicLabel(topic),
        }))
        .filter((topic) => topic.label)
    },
    [bookmarks, topicLabel]
  )

  // The nav bookmark tag opens a modal: edit if the item is already bookmarked,
  // otherwise create (with the item pre-selected). Refresh indicators on save.
  const handleBookmark = useCallback(
    (row) => {
      const existing = bookmarks.getBookmark(row._id)
      if (existing) {
        openModal('edit-bookmark', { bookmark: existing, onSaved: bookmarks.reload })
      } else {
        openModal('create-bookmark', { item: row, onSaved: bookmarks.reload })
      }
    },
    [bookmarks, openModal]
  )

  const handleNewBookmark = useCallback(() => {
    openModal('create-bookmark', { onSaved: bookmarks.reload })
  }, [openModal, bookmarks])
  const handleNewTopic = useCallback(() => {
    openModal('create-topic', { onSaved: bookmarks.reload })
  }, [openModal, bookmarks])
  const handleEditTopics = useCallback(() => {
    openModal('edit-topics', { onChanged: bookmarks.reload })
  }, [openModal, bookmarks])
  const navActions = useMemo(
    () => [
      {
        key: 'add-window',
        label: 'Add window',
        icon: <IconAddWindow />,
        onClick: handleAddWindow,
      },
      {
        key: 'new-bookmark',
        label: 'New bookmark',
        icon: <IconBookmarkPlus />,
        onClick: handleNewBookmark,
      },
      {
        key: 'new-topic',
        label: 'New topic',
        icon: <IconFolderPlus />,
        onClick: handleNewTopic,
      },
      {
        key: 'edit-topics',
        label: 'Manage topics',
        icon: <IconFolderEdit />,
        onClick: handleEditTopics,
      },
    ],
    [handleAddWindow, handleNewBookmark, handleNewTopic, handleEditTopics]
  )

  return (
    <MainLayout
      storageId="code-layout"
        navLocation={
          <NavLocation>
            <div className="code-locationbar">
              <CodeBreadcrumb trail={workspace.trail} onNavigate={workspace.goTo} />
              <CodeViewToggle mode={workspace.mode} onChange={workspace.setMode} />
            </div>
          </NavLocation>
        }
        navActions={navActions}
        navDock={
          <GridDock
            items={grid.minimized}
            getTitle={getTitle}
            onRestore={grid.restoreWindow}
            onClose={grid.closeWindow}
          />
        }
        nav={
          <NavList>
            <NavFilterLead
              open={filterOpen}
              active={workspace.filter.active}
              onToggle={toggleFilter}
              onClear={workspace.clearFilter}
              clearDisabled={!workspace.filter.active}
            />
            <div
              className={
                'nav-filter-match' +
                (!filterOpen && !topicsData.topics.length ? ' is-last' : '')
              }
            >
              <span className="code-filter-label">Match</span>
              <div className="ct-toggle" role="group" aria-label="Match mode">
                <button
                  type="button"
                  className={'ct-toggle-btn' + (workspace.filter.matchMode === 'or' ? ' is-active' : '')}
                  onClick={() => workspace.setMatchMode('or')}
                >
                  Any
                </button>
                <button
                  type="button"
                  className={'ct-toggle-btn' + (workspace.filter.matchMode === 'and' ? ' is-active' : '')}
                  onClick={() => workspace.setMatchMode('and')}
                >
                  All
                </button>
              </div>
            </div>
            {topicsData.topics.length ? (
              <div className={'nav-topic-filter code-filter-facet' + (filterOpen ? ' is-with-panel' : '')}>
                <span className="code-filter-label">Topics</span>
                <MultiSelect
                  value={workspace.filter.values.topics || []}
                  onChange={(next) => workspace.setFacet('topics', next)}
                  options={topicsData.topics}
                  getOptionValue={(t) => t._id}
                  getOptionLabel={topicLabelFor(topicsData.topics)}
                  placeholder="Any topic"
                  clearQueryOnSelect={false}
                  onOpen={topicsData.reload}
                />
                <label
                  className="code-filter-check"
                  title="Also match bookmarks filed under topics inside the ones you pick"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(workspace.filter.includeTopicDescendants)}
                    onChange={(event) =>
                      workspace.setIncludeTopicDescendants(event.target.checked)
                    }
                  />
                  Include descendants
                </label>
              </div>
            ) : null}
            {filterOpen ? (
              <CodeFilterPanel
                values={workspace.filter.values}
                onChange={workspace.setFacet}
                text={workspace.filter.text}
                onTextChange={workspace.setText}
                bookmarkedOnly={workspace.filter.bookmarkedOnly}
                onBookmarkedOnlyChange={workspace.setBookmarkedOnly}
                authorOptions={filterOptions.authorOptions}
                textTypeOptions={filterOptions.textTypeOptions}
              />
            ) : null}
            <CodeNav
              status={workspace.status}
              rows={workspace.rows}
              atRoot={workspace.atRoot}
              mode={workspace.mode}
              selectedId={workspace.selectedId}
              hasChildren={workspace.hasChildren}
              depthOf={workspace.depthOf}
              isExpanded={workspace.isExpanded}
              isBookmarked={isRowBookmarked}
              bookmarkTopics={bookmarkTopics}
              isDimmed={workspace.isDimmed}
              onOpen={handleOpen}
              onForward={handleForward}
              onToggleCollapse={workspace.toggleCollapse}
              onBookmark={handleBookmark}
              scrollToId={workspace.revealSignal.id}
              scrollNonce={workspace.revealSignal.nonce}
            />
          </NavList>
        }
        display={
          <div className="sr-grid-wrap">
            {notice && <div className="sr-grid-notice">{notice}</div>}
            <div className="sr-grid-body">
              <WindowGrid
                grid={grid}
                renderWindow={renderWindow}
                getTitle={getTitle}
                empty="Select a standard to get started."
              />
            </div>
          </div>
        }
      />
  )
}

export default CodePage
